import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import parse from "html-react-parser";
import StaticMessageCard from "./CommentPostedCard";
import { Editor } from "draft-js";
import { EditorState } from "draft-js";
import useSavedPostsStore from "../../zustand-stores/SavedPostsStore";
import useOutsideClick from "../../hooks/useOutsideClick";
import { api } from "../../actions/api";
import { toast } from "react-toastify";
import CommentInput from "./CommentImput";
import linkifyHtml from "linkify-html";
import EditPostModal from "./EditPostModal";
import JournalSharedTribePost from "../../Components/Tribe/JournalSharedTribePost";
import { useQueryClient } from "@tanstack/react-query";
import useTribePostStore from "../../zustand-stores/TribePostStore";
import PostViewCount from "./PostViewCount";
import ReactionButton from "./ReactionButton";
import useReactions from "../../hooks/useReactions";
import { useTogglePinPost } from "../../Controllers/Tribe/TribePostController";
import {
  getReactionSummary,
  getTotalReactionCount,
} from "../../helpers/reactionsHelper";
import { ReactionSummary } from "./ReactionSummary";
import BaseModal from "../Modals/BaseModal";
import RenderMediaGroup from "./RenderMediaGroup";
import LiveRelativeTime from "./LiveRelativeTime";
import { humanizeDate } from "../../helpers/DateHelper";
import {
  createParsedHtmlParseOptions,
  extractHostedVideoEmbeds,
} from "../../Utils/parseLinkifiedHtmlWithVideoEmbeds";
import HostedVideoEmbed from "../Common/HostedVideoEmbed";
import ChatLinkCard from "../Common/ChatLinkCard";
import { isHostedBookmarkCardLink } from "../../Utils/videoEmbedUrl";

const JournalCard = ({
  post,
  isAdminOnly,
  onPostSaved,
  onPostHide,
  userProfile,
  groupId,
  members,
  onPostUpdated,
  viewCount,
  onUserClicked,
  onPost,
  // selectedCommentId,
  // openCommentBox
  isPost,
  onGroupClicked,
}) => {
  // Basic state
  const [liked, setLiked] = useState(post.liked_by_current_user || false);
  const [likeCount, setLikeCount] = useState(post.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const { comments_count } = post;
  const [commentsCount, setCommentsCount] = useState(comments_count);
  const [isBookmarked, setIsBookmarked] = useState(post.is_bookmarked || false);
  const [isPinnedByCurrentUser, setIsPinnedByCurrentUser] = useState(
    post.is_pinned_by_current_user || false,
  );
  const [isPinned, setIsPinned] = useState(Boolean(post?.pinned_by_type));
  const [isHidden, setIsHidden] = useState(post.is_hidden || false);
  const [isLoading, setIsLoading] = useState(false);
  const [postCommentsData, setPostCommentsData] = useState({
    comments: [],
    meta: {},
  });
  const [loadingComments, setLoadingComment] = useState(false);

  // Reactions state
  const [postReactions, setPostReactions] = useState(post.reactions || null);
  const { reactions: availableReactions, isReady: reactionsReady } =
    useReactions();

  // Countdown for scheduled posts
  const [countdown, setCountdown] = useState("");

  //pin post
  const isPinnedByAdminOrSuperAdmin =
    post?.pinned_by_type === "super_admin" ||
    post?.pinned_by_type === "group_admin";
  const isPinnedByCurrentAdmin = post.pinned_by?.id === userProfile?.id;
  const canTogglePin = !isPinnedByAdminOrSuperAdmin || isPinnedByCurrentAdmin;
  const [showPinButtonTooltip, setShowPinButtonTooltip] = useState(false);

  // Update reactions when post changes
  useEffect(() => {
    if (post.reactions) {
      setPostReactions(post.reactions);
      const currentReaction = post.reactions.current_user_reaction;
      if (currentReaction) {
        setLiked(true);
      }
    } else {
      setLiked(post.liked_by_current_user || false);
      setLikeCount(post.likes_count || 0);
    }
  }, [post.reactions, post.liked_by_current_user, post.likes_count]);

  const [showProfileImageModal, setShowProfileImageModal] = useState(false);
  const [commentsLoaded, setCommentsLoaded] = useState(false);

  const queryClient = useQueryClient();
  const { setRefetchPost } = useTribePostStore();

  const { mutate: togglePinPost } = useTogglePinPost();

  const [showPinnedTooltip, setShowPinnedTooltip] = useState(false);

  const { savedPosts, toggleSavePost } = useSavedPostsStore();
  const dropdownRef = useRef(null);
  const postTextRef = useRef(null);
  const [isPostTextTruncated, setIsPostTextTruncated] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [publishLabel, setPublishLabel] = useState("Publishes in");

  useOutsideClick(dropdownRef, () => {
    setShowDropdown(false);
  });

  const [editorState, setEditorState] = useState(() =>
    EditorState.createEmpty(),
  );

  // Handle reaction update (called by ReactionButton)
  const handleReactionUpdate = useCallback((reactionsData) => {
    if (reactionsData) {
      setPostReactions({ ...reactionsData });
      const hasReaction = reactionsData.current_user_reaction !== null;
      setLiked(hasReaction);
      setLikeCount(reactionsData.total_count || 0);
    }
  }, []);

  // legacy like
  const toggleLike = async () => {
    try {
      setIsLoading(true);
      await api.post(`user/post/like?id=${post.id}`);
      setLiked((prev) => !prev);
      setLikeCount((prev) => (liked ? prev - 1 : prev + 1));
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Failed to toggle like. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // comment like
  const handleCommentLikeUpdated = (commentId, liked, likesCount) => {
    setPostCommentsData((prev) => ({
      ...prev,
      comments: prev.comments.map((c) =>
        c.id === commentId
          ? {
              ...c,
              liked_by_current_user: liked,
              likes_count: likesCount,
            }
          : c,
      ),
    }));
  };

  // bookmark toggle
  const toggleBookmark = async () => {
    try {
      setIsLoading(true);
      await api.post(`user/post/save?id=${post.id}`);
      const newBookmarkStatus = !isBookmarked;

      setIsBookmarked(newBookmarkStatus);

      if (onPostSaved) {
        onPostSaved(post.id, newBookmarkStatus);
      }

      toggleSavePost({
        id: post.id,
        postImage: post.media?.[0] || "",
        content: post.text || "This is placeholder content...",
      });

      toast.success(
        newBookmarkStatus
          ? "Post saved successfully!"
          : "Post removed from saved items",
      );
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
    } catch (error) {
      console.error("Error toggling bookmark:", error);
      setIsBookmarked(!isBookmarked);
      toast.error("Failed to toggle bookmark. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditPost = () => {
    setShowEditModal(true);
    setShowDropdown(false);
  };

  const handlePostUpdated = (updatedPost) => {
    if (onPostUpdated) {
      onPostUpdated(updatedPost);
    }
    queryClient.invalidateQueries({ queryKey: ["tribePostDetails", post.id] });
    setShowEditModal(false);
  };

  const handleDeletePost = async () => {
    try {
      setIsLoading(true);
      await api.delete(`user/delete-post/${post.id}`);
      if (onPostHide) {
        onPostHide(post.id);
      }
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
      toast.success("Post Deleted successfully!");
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleHidePost = async () => {
    try {
      setIsLoading(true);
      await api.post(`user/post/hide?id=${post.id}`);
      if (onPostHide) {
        onPostHide(post.id);
      }
      setIsHidden(true);
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
      toast.success("Post hidden successfully!");
    } catch (error) {
      console.error("Error hiding post:", error);
      toast.error("Failed to hide post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnHidePost = async () => {
    try {
      setIsLoading(true);
      await api.post(`user/post/unhide?id=${post.id}`);
      setIsHidden(false);
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
      toast.success("Post unhidden successfully!");
    } catch (error) {
      console.error("Error unhiding post:", error);
      toast.error("Failed to unhide post. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const linkConvert = (content) => {
    return linkifyHtml(content, {
      target: "_blank",
      rel: "noopener noreferrer",
    });
  };

  const renderPostTextHtml = (html) => {
    if (!html) return null;
    return parse(
      html,
      createParsedHtmlParseOptions(
        (domNode) => {
          if (
            domNode.name === "span" &&
            domNode.attribs?.class?.includes?.("comment-mention")
          ) {
            const userId = domNode.attribs["data-user-id"];
            const text =
              (domNode.children || [])
                .map((c) => (c.type === "text" ? c.data : ""))
                .join("") || "@";
            return (
              <span
                role="button"
                tabIndex={0}
                className="comment-mention"
                onClick={() =>
                  onUserClicked?.(Number(userId), null, true, groupId)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onUserClicked?.(Number(userId), null, true, groupId);
                  }
                }}
              >
                {text}
              </span>
            );
          }
          return undefined;
        },
        { keepLinkAnchorsInline: true },
      ),
    );
  };

  const postLinkedHtml = useMemo(
    () => (post.text ? linkConvert(post.text) : ""),
    [post.text],
  );

  const {
    videoUrls: postVideoUrls,
    linkUrls: postLinkUrls,
    textHtml: postTextHtml,
    hasText: postHasText,
  } = useMemo(
    () =>
      extractHostedVideoEmbeds(postLinkedHtml, { keepLinkAnchorsInline: true }),
    [postLinkedHtml],
  );

  useLayoutEffect(() => {
    if (expanded) {
      setIsPostTextTruncated(false);
      return;
    }
    const el = postTextRef.current;
    if (!el) return;
    setIsPostTextTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [postTextHtml, expanded, postHasText]);

  const shouldShowPostToggle = expanded || isPostTextTruncated;

  useEffect(() => {
    setExpanded(false);
  }, [post.id]);

  // central comment fetcher
  const fetchComments = async (openAfterFetch = false) => {
    try {
      setLoadingComment(true);
      const res = await api.get(`user/post/comments`, {
        params: { post_id: post.id, per_page: 10, page: 1 },
      });

      setPostCommentsData({
        comments: res.data.data.comments,
        meta: res.data.data.meta,
      });
      setCommentsLoaded(true);

      if (openAfterFetch) {
        setShowComments(true);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Failed to fetch comments. Please try again.");
    } finally {
      setLoadingComment(false);
    }
  };

  // auto-load comments on mount/post change (for preview)
  useEffect(() => {
    setPostCommentsData({ comments: [], meta: {} });
    setCommentsLoaded(false);
    fetchComments(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  // open/close main comments section
  const handleCommentPressed = () => {
    if (!commentsLoaded) {
      fetchComments(true);
    } else {
      setShowComments((prev) => !prev);
    }
  };

  const loadMoreComments = async () => {
    if (
      !loadingComments &&
      postCommentsData.meta?.current_page &&
      postCommentsData.meta?.last_page &&
      postCommentsData.meta.current_page < postCommentsData.meta.last_page
    ) {
      try {
        setLoadingComment(true);
        const nextPage = postCommentsData.meta.current_page + 1;
        const params = {
          post_id: post.id,
          per_page: 10,
          page: nextPage,
        };

        const res = await api.get(`user/post/comments`, {
          params,
        });

        setPostCommentsData((prev) => ({
          ...prev,
          comments: [...prev.comments, ...res.data.data.comments],
          meta: res.data.data.meta,
        }));
      } catch (error) {
        console.error("Error fetching comments:", error);
        toast.error("Failed to fetch comments. Please try again.");
      } finally {
        setLoadingComment(false);
      }
    }
  };

  const refreshComment = async ({
    isEditing = false,
    deleting = false,
  } = {}) => {
    if (!isEditing && !deleting) {
      setCommentsCount((prev) => prev + 1);
    }

    if (deleting) {
      setCommentsCount((prev) => prev - 1);
    }

    try {
      setIsLoading(true);
      const res = await api.get(`user/post/comments`, {
        params: { post_id: post.id, per_page: 10, page: 1 },
      });

      setPostCommentsData({
        comments: res.data.data.comments,
        meta: res.data.data.meta,
      });
      setShowComments(true);
      setCommentsLoaded(true);
      queryClient.invalidateQueries({
        queryKey: ["tribePostDetails", post.id],
      });
    } catch (error) {
      console.error("Error refreshing comments:", error);
      toast.error("Failed to refresh comments. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const formatLikes = (num) => {
    if (!num) return null;
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + "K";
    }
    return num.toString();
  };

  // derive latest 2 comments for preview
  const previewComments = useMemo(() => {
    if (postCommentsData.comments?.length) {
      return postCommentsData.comments.slice(0, 2);
    }

    if (post.latest_comments?.length) {
      return post.latest_comments.slice(0, 2);
    }

    return [];
  }, [postCommentsData.comments, post.latest_comments]);

  // Refetch post details after modal actions
  const handleJournalModalClose = async () => {
    queryClient.invalidateQueries({ queryKey: ["tribePostDetails", post.id] });
  };

  // Get tooltip text for pinned post
  const getPinnedTooltip = () => {
    // Check if pinned by current user directly
    if (isPinnedByCurrentUser || post.is_pinned_by_current_user) {
      return "Pinned by you";
    }

    const pinnedByType = post?.pinned_by_type;
    if (!pinnedByType) {
      return "Pinned post";
    }

    // Check if pinned by group_admin and current user is a group admin
    if (pinnedByType === "group_admin" && userProfile?.id && members) {
      const isCurrentUserGroupAdmin = members.some(
        (member) =>
          member.role === "Admin" && member.user?.id === userProfile.id,
      );
      if (isCurrentUserGroupAdmin) {
        return "Pinned by you";
      }
    }

    // Format the pinned_by_type: replace underscores with spaces and capitalize
    const formattedType = pinnedByType
      .replace(/_/g, " ") // Replace underscores with spaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return `Pinned by ${formattedType}`;
  };
  // Countdown effect for scheduled posts
  useEffect(() => {
    const scheduled = post.scheduled_post;

    if (
      !scheduled ||
      scheduled.is_published ||
      !scheduled.scheduled_display_datetime
    ) {
      setCountdown("");
      return;
    }

    const targetMs = new Date(scheduled.scheduled_display_datetime).getTime();

    const updateCountdown = () => {
      const nowMs = Date.now();
      let diff = targetMs - nowMs;

      if (diff <= 0) {
        setCountdown("");
        setPublishLabel(
          "Published. Please refresh the page to view you Published Post",
        );
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      let label = "";
      if (days > 0) {
        label += `${days}d `;
      }
      label += `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;

      setCountdown(label);
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [post.scheduled_post]);

  return (
    <div
      className="journal-card"
      style={{
        backgroundColor: "#1C2A37",
        borderRadius: "24px",
        marginLeft: "24px",
        marginRight: "24px",
        marginBottom: "31px",
        padding: "12px 24px",
      }}
    >
      {/* Main content wrapper */}
      <div className="d-flex flex-column">
        {/* Header row with user info, reactions summary, and dropdown */}
        <div className="d-flex align-items-center" style={{ gap: "12px" }}>
          {/* User avatar */}
          <div style={{ position: "relative" }}>
            <img
              src={post.created_by?.image || "icons/james.svg"}
              alt="User"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                objectFit: "cover",
                cursor: "pointer",
              }}
              onClick={() => {
                if (post.created_by?.image) {
                  setShowProfileImageModal(true);
                }
              }}
            />
            {post.created_by?.user_level && (
              <div
                style={{
                  position: "absolute",
                  bottom: "0px",
                  right: "-5px",
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: "#008CFF",
                  border: "2px solid #fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "10px",
                }}
              >
                {post.created_by.user_level}
              </div>
            )}
          </div>

          {/* User name, date / countdown */}
          <div
            className="d-flex flex-column"
            style={{ marginTop: "1px", marginBottom: "1px" }}
          >
            <span
              className="text-body-b2"
              style={{
                color: "#d0d8df",
                fontWeight: "700",
              }}
            >
              <span
                style={{ cursor: "pointer" }}
                onClick={() => {
                  if (onUserClicked && post.created_by?.id) {
                    onUserClicked(
                      post.created_by.id,
                      post.created_by,
                      true,
                      post.group?.id ?? post.group_id ?? groupId,
                    );
                  }
                }}
              >
                {post.created_by?.full_name || "Unknown User"}
              </span>

              {post.tagged_users?.length > 0 && (
                <>
                  {"  > "}
                  {post.tagged_users.map((user, index) => (
                    <span key={user.id}>
                      <span
                        style={{
                          cursor: onUserClicked ? "pointer" : "default",
                        }}
                        onClick={() => {
                          if (onUserClicked && user?.id) {
                            onUserClicked(
                              user.id,
                              user,
                              true,
                              post.group?.id ?? post.group_id ?? groupId,
                            );
                          }
                        }}
                      >
                        {user.full_name}
                      </span>
                      {index < post.tagged_users.length - 1 ? ", " : ""}
                    </span>
                  ))}
                </>
              )}

              {isPost && post.group?.name && (
                <>
                  {"  > "}
                  <span
                    onClick={() => {
                      if (onGroupClicked && post.group?.id) {
                        onGroupClicked(post.group.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {post.group.name}
                  </span>
                </>
              )}
            </span>

            {/* show published-in countdown instead of created_at for scheduled posts */}
            {post.scheduled_post &&
            post.scheduled_post.is_published === false &&
            countdown ? (
              <div
                style={{
                  marginTop: "4px",
                  display: "inline-flex",
                  alignItems: "center",
                  padding: "3px 10px",
                  borderRadius: "999px",
                  backgroundColor: "#111827",
                  border: "1px solid #374151",
                  color: "#E5E7EB",
                  fontSize: "12px",
                  width: "fit-content",
                }}
              >
                <span style={{ marginRight: "4px" }}>{publishLabel}</span>
                <strong>{countdown}</strong>
              </div>
            ) : (
              <LiveRelativeTime
                date={post.created_at}
                className="text-body-b4"
              />
            )}
          </div>

          {/* Right-aligned group: Pinned Icon, and Dropdown */}
          <div
            className="d-flex align-items-center"
            style={{
              marginLeft: "auto",
              gap: "12px",
            }}
          >
            {isPinned && (
              <div
                className="position-relative"
                style={{
                  cursor: "pointer",
                }}
                onMouseEnter={() => setShowPinnedTooltip(true)}
                onMouseLeave={() => setShowPinnedTooltip(false)}
              >
                <img src="/icons/unpin.svg" alt="Pinned Post" />
                {/* Custom tooltip */}
                {showPinnedTooltip && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: "100%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      marginBottom: "8px",
                      padding: "6px 12px",
                      backgroundColor: "rgba(0, 0, 0, 0.9)",
                      color: "#fff",
                      fontSize: "12px",
                      borderRadius: "6px",
                      whiteSpace: "nowrap",
                      zIndex: 1000,
                    }}
                  >
                    {getPinnedTooltip()}
                    {/* Tooltip arrow */}
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "6px solid transparent",
                        borderRight: "6px solid transparent",
                        borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            {/* Options dropdown */}
            <div className="position-relative" ref={dropdownRef}>
              <img
                src="icons/three-dots-post.svg"
                alt="three-dots"
                style={{ cursor: "pointer" }}
                onClick={() => setShowDropdown((prev) => !prev)}
              />
              {showDropdown && (
                <div
                  className="dropdown-menu"
                  style={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    backgroundColor: "#1C2A37",
                    border: "1px solid #2A4052",
                    borderRadius: "10px",
                    padding: "8px 0",
                    zIndex: 1000,
                    maxWidth: "190px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    // justifyContent: "start",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "start",
                    }}
                  >
                    {/* Pin / Unpin – disabled if pinned by some other admin/super admin */}
                    <div
                      className="position-relative"
                      onMouseEnter={() => {
                        if (!canTogglePin && isPinnedByAdminOrSuperAdmin) {
                          setShowPinButtonTooltip(true);
                        }
                      }}
                      onMouseLeave={() => setShowPinButtonTooltip(false)}
                    >
                      <div
                        className="dropdown-item d-flex align-items-center justify-content-start px-3 py-2 text-white"
                        style={{
                          cursor: canTogglePin ? "pointer" : "not-allowed",
                          marginBottom: "4px",
                          transition: "background-color 0.2s ease",
                          width: "100%",
                          opacity: canTogglePin ? 1 : 0.5,
                        }}
                        onClick={() => {
                          if (!canTogglePin) return; // blocked when pinned by other admin

                          togglePinPost({ post_id: post.id });
                          setIsPinned(!isPinned);
                          setIsPinnedByCurrentUser(!isPinnedByCurrentUser);
                          onPost && onPost();
                          setShowDropdown(false);
                        }}
                      >
                        <img
                          src={
                            // Show "Unpin" icon when this post is pinned by YOU (admin) OR personally
                            isPinnedByCurrentAdmin ||
                            post.is_pinned_by_current_user ||
                            isPinned
                              ? "/icons/unpin.svg"
                              : "/icons/pin.svg"
                          }
                          alt={
                            isPinnedByCurrentAdmin ||
                            post.is_pinned_by_current_user ||
                            isPinned
                              ? "Pinned"
                              : "Unpinned"
                          }
                          style={{
                            marginRight: "8px",
                            width: "18px",
                            opacity: canTogglePin ? 1 : 0.7,
                          }}
                        />
                        <span>
                          {isPinnedByCurrentAdmin ||
                          post.is_pinned_by_current_user ||
                          isPinned
                            ? "Unpin Post"
                            : "Pin Post"}
                        </span>
                      </div>

                      {/* Custom tooltip for disabled state */}
                      {!canTogglePin &&
                        isPinnedByAdminOrSuperAdmin &&
                        showPinButtonTooltip && (
                          <div
                            style={{
                              position: "absolute",
                              bottom: "100%",
                              left: "50%",
                              transform: "translateX(-50%)",
                              marginBottom: "8px",
                              padding: "6px 12px",
                              backgroundColor: "rgba(0, 0, 0, 0.9)",
                              color: "#fff",
                              fontSize: "12px",
                              borderRadius: "6px",
                              whiteSpace: "nowrap",
                              zIndex: 1000,
                            }}
                          >
                            {/* e.g. "Pinned by Super Admin" / "Pinned by Group Admin" */}
                            {getPinnedTooltip()}
                            <div
                              style={{
                                position: "absolute",
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 0,
                                height: 0,
                                borderLeft: "6px solid transparent",
                                borderRight: "6px solid transparent",
                                borderTop: "6px solid rgba(0, 0, 0, 0.9)",
                              }}
                            />
                          </div>
                        )}
                    </div>
                    <div
                      className="dropdown-item d-flex align-items-center justify-content-start px-3 py-2 text-white"
                      style={{
                        cursor: "pointer",
                        marginBottom: "4px",
                        transition: "background-color 0.2s ease",
                        width: "100%",
                      }}
                      onClick={() => {
                        toggleBookmark();
                        setShowDropdown(false);
                      }}
                    >
                      <img
                        src={
                          isBookmarked
                            ? "/icons/bookmarked.svg"
                            : "/icons/bookmark-not-saved.svg"
                        }
                        alt={isBookmarked ? "Saved" : "Save Post"}
                        style={{ marginRight: "8px", width: "18px" }}
                      />
                      {isBookmarked ? "Unsave Post" : "Save Post"}
                    </div>
                    {!post.is_created_by_current_user && (
                      <div
                        className="dropdown-item d-flex align-items-center justify-content-start px-3 py-2 text-white"
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          width: "100%",
                        }}
                        onClick={() => {
                          isHidden ? handleUnHidePost() : handleHidePost();
                          setShowDropdown(false);
                        }}
                      >
                        <img
                          src={
                            isHidden ? "/icons/eye.svg" : "/icons/eye-close.svg"
                          }
                          alt={isHidden ? "Unhide Post" : "Hide Post"}
                          style={{ marginRight: "8px", width: "18px" }}
                        />
                        {isHidden ? "Unhide Post" : "Hide Post"}
                      </div>
                    )}

                    {post.is_created_by_current_user && (
                      <div
                        className="dropdown-item d-flex align-items-center justify-content-start px-3 py-2 text-white"
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          width: "100%",
                        }}
                        onClick={() => {
                          handleEditPost();
                          setShowDropdown(false);
                        }}
                      >
                        <img
                          src={"/icons/chat-edit.svg"}
                          alt={"Edit Post"}
                          style={{ marginRight: "8px", width: "18px" }}
                        />
                        Edit Post
                      </div>
                    )}

                    {post.is_created_by_current_user && (
                      <div
                        className="dropdown-item d-flex align-items-center justify-content-start px-3 py-2 text-white"
                        style={{
                          cursor: "pointer",
                          transition: "background-color 0.2s ease",
                          width: "100%",
                        }}
                        onClick={() => {
                          handleDeletePost();
                          setShowDropdown(false);
                        }}
                      >
                        <img
                          src={"/icons/delete-icon.svg"}
                          alt={"Delete Post"}
                          style={{ marginRight: "8px", width: "18px" }}
                        />
                        Delete Post
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr
          className="my-3 my-sm-3"
          style={{
            borderTop: "1px solid var(--Bg-Border, #2A4052)",
            opacity: 1,
            width: "calc(100% + 48px)",
            marginLeft: "-24px",
            marginRight: "-24px",
          }}
        />

        {post?.is_journal_shared ? (
          <JournalSharedTribePost
            post={post}
            onModalClose={handleJournalModalClose}
          />
        ) : (
          <>
            {postVideoUrls.map((url, i) => (
              <div
                key={`post-embed-video-${post.id}-${i}`}
                className="hosted-video-embed-block"
              >
                <HostedVideoEmbed url={url} />
              </div>
            ))}
            {postLinkUrls.map((url, i) => (
              <div
                key={`post-embed-link-${post.id}-${i}`}
                className="hosted-video-embed-block"
              >
                {isHostedBookmarkCardLink(url) ? (
                  <HostedVideoEmbed url={url} />
                ) : (
                  <ChatLinkCard url={url} />
                )}
              </div>
            ))}
            {postHasText ? (
              <div
                ref={postTextRef}
                className={`post-text post-text-content${
                  expanded ? "" : " post-text-content--collapsed"
                }`}
                style={{
                  color: "#C8CED3",
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                  marginBottom: "10px",
                }}
              >
                {renderPostTextHtml(postTextHtml)}
              </div>
            ) : null}
          </>
        )}

        {shouldShowPostToggle && (
          <div
            className="post-text"
            style={{ marginTop: "8px", cursor: "pointer" }}
            onClick={() => setExpanded(!expanded)}
          >
            <svg
              width="16"
              height="17"
              viewBox="0 0 16 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_57395_31842)">
                <path
                  d={
                    expanded
                      ? "M12.6663 7.35547H3.33301V8.6888H12.6663V7.35547Z"
                      : "M12.6663 8.6888H8.66634V12.6888H7.33301V8.6888H3.33301V7.35547H7.33301V3.35547H8.66634V7.35547H12.6663V8.6888Z"
                  }
                  fill="#008CFF"
                />
              </g>
              <defs>
                <clipPath id="clip0_57395_31842">
                  <rect
                    width="16"
                    height="16"
                    fill="white"
                    transform="translate(0 0.0214844)"
                  />
                </clipPath>
              </defs>
            </svg>
            <span
              style={{
                fontSize: "16px",
                color: "#008CFF",
                marginLeft: "12px",
              }}
            >
              {expanded ? "See Less" : "See More"}
            </span>
          </div>
        )}

        <div>
          {/* Media display */}
          <RenderMediaGroup media={post.media} />

          {/* Reaction Summary - Display all reactions with counts */}
          <ReactionSummary
            reactionsData={postReactions}
            availableReactions={availableReactions}
          />

          {/* Post text */}
        </div>

        {/* Action buttons */}
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            {/* Reaction button */}
            {reactionsReady ? (
              <ReactionButton
                type="post"
                targetId={post.id}
                reactionsData={postReactions}
                availableReactions={availableReactions}
                onReactionUpdate={handleReactionUpdate}
                formatLikes={formatLikes}
              />
            ) : (
              <div
                className="d-flex align-items-center gap-2"
                style={{ cursor: "pointer" }}
                onClick={() => {
                  toggleLike();
                }}
              >
                <img
                  src={liked ? "icons/post-liked.svg" : "icons/post-like.svg"}
                  alt={liked ? "liked" : "like"}
                  style={{ width: "24px", height: "24px" }}
                />
                <span style={{ color: "#D0D8DF" }} className="counts-text">
                  {formatLikes(likeCount)}
                </span>
              </div>
            )}

            {/* Comment button */}
            <div
              className="d-flex align-items-center gap-2"
              style={{ cursor: "pointer" }}
              onClick={handleCommentPressed}
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 25 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.9 19.11C16.84 22.17 12.32 22.83 8.61 21.12C8.06 20.89 7.62 20.72 7.19 20.72C6.01 20.72 4.53 21.87 3.76 21.11C2.99 20.34 4.15 18.86 4.15 17.67C4.15 17.24 3.97 16.8 3.75 16.25C2.04 12.55 2.7 8.02 5.76 4.97C9.66 1.07 16 1.07 19.9 4.97C23.81 8.88 23.8 15.21 19.9 19.11Z"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M16.77 12.45H16.78"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.76 12.45H12.77"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M8.75 12.45H8.76"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span style={{ color: "#D0D8DF" }} className="counts-text">
                {formatLikes(commentsCount)}
              </span>
              <span
                className="react-text"
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#D0D8DF",
                }}
              >
                Comment
              </span>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* View Count */}
            {viewCount !== undefined && <PostViewCount viewCount={viewCount} />}
            {/* Bookmark button */}
            <div onClick={toggleBookmark} style={{ cursor: "pointer" }}>
              {isBookmarked ? (
                <img
                  src="/icons/bookmarked.svg"
                  alt="saved"
                  style={{ width: "24px", height: "24px" }}
                />
              ) : (
                <img
                  src="/icons/bookmark-not-saved.svg"
                  alt="not-saved"
                  style={{ width: "24px", height: "24px" }}
                />
              )}
            </div>
          </div>
        </div>

        <hr
          className="my-3 my-sm-3"
          style={{
            borderTop: "1px solid var(--Bg-Border, #2A4052)",
            opacity: 1,
            width: "calc(100% + 48px)",
            marginLeft: "-24px",
            marginRight: "-24px",
          }}
        />

        {/* latest comments preview – hidden when main comments are open */}
        {!showComments && previewComments.length > 0 && (
          <div className="d-flex flex-column gap-2">
            <div className="d-flex flex-column gap-3 mt-2">
              {previewComments.map((comment) => (
                <StaticMessageCard
                  key={comment.id}
                  userProfile={userProfile}
                  comment={comment}
                  refreshMainComment={refreshComment}
                  onLikeUpdated={handleCommentLikeUpdated}
                  members={members}
                  onUserClicked={onUserClicked}
                  groupId={post.group?.id ?? groupId}
                />
              ))}
            </div>

            {commentsCount > 2 && (
              <div
                className="text-caption-c2"
                style={{
                  color: "rgb(0, 140, 255)",
                  cursor: "pointer",
                  marginTop: "10px",
                  fontSize: "14px",
                }}
                onClick={handleCommentPressed}
              >
                View all comments
              </div>
            )}
          </div>
        )}

        {/* Comments section */}
        {showComments && (
          <div>
            <hr
              className="my-2 my-sm-3"
              style={{
                borderTop: "1px solid var(--Bg-Border, #2A4052)",
                opacity: 1,
                width: "calc(100% + 48px)",
                marginLeft: "-24px",
                marginRight: "-24px",
              }}
            />
            <div className="d-flex flex-column gap-2">
              {postCommentsData.comments.length > 0 ? (
                <>
                  {postCommentsData.comments.map((comment) => (
                    <StaticMessageCard
                      key={comment.id}
                      userProfile={userProfile}
                      comment={comment}
                      refreshMainComment={refreshComment}
                      onLikeUpdated={handleCommentLikeUpdated}
                      members={members}
                      onUserClicked={onUserClicked}
                      groupId={post.group?.id ?? groupId}
                    />
                  ))}
                  {postCommentsData.meta.current_page <
                  postCommentsData.meta?.last_page ? (
                    <p
                      className="mb-0"
                      style={{
                        color: "rgb(0, 140, 255)",
                        cursor: "pointer",
                        fontSize: "14px",
                      }}
                      onClick={loadMoreComments}
                    >
                      View more comments...
                    </p>
                  ) : null}
                </>
              ) : (
                <p className=" text-center " style={{ color: "#D0D8DF" }}>
                  No comments yet
                </p>
              )}
            </div>

            <hr
              className="my-2 my-sm-3"
              style={{
                borderTop: "1px solid var(--Bg-Border, #2A4052)",
                opacity: 1,
                width: "calc(100% + 48px)",
                marginLeft: "-24px",
                marginRight: "-24px",
              }}
            />
            <CommentInput
              postId={post.id}
              groupId={post.group?.id ?? groupId}
              userProfile={userProfile}
              onCommentPosted={refreshComment}
              members={members}
            />
          </div>
        )}
      </div>

      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        post={post}
        groupId={post.group?.id ?? post.group_id ?? groupId}
        userProfile={userProfile}
        members={members || []}
        onPostUpdated={handlePostUpdated}
      />

      {/* Profile Image Preview Modal */}
      <BaseModal
        open={showProfileImageModal}
        onClose={() => setShowProfileImageModal(false)}
        title="Profile Picture"
        width={600}
        maxHeight="90vh"
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "20px",
          }}
        >
          <img
            src={post.created_by?.image || "icons/james.svg"}
            alt={post.created_by?.full_name || "User"}
            style={{
              maxWidth: "100%",
              maxHeight: "70vh",
              borderRadius: "8px",
              objectFit: "contain",
            }}
          />
        </div>
      </BaseModal>
    </div>
  );
};

export default JournalCard;
